import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema, surfaceSchema } from "./_envelope";

/**
 * Fires once per route, on the first layout that lands while the page is
 * actually on screen.
 *
 * Why this exists alongside `paywall_viewed` / `entrypoint_viewed`: those fire
 * on React mount, and Owlet preloads Felt pages at app launch, so a mount can
 * happen minutes before — or entirely without — the user seeing the page.
 * `content_visible` rides the same `ResizeObserver` that publishes
 * `layoutUpdated` to native, so it only lands once the native container has
 * actually laid the page out. Comparing its volume against `paywall_viewed`
 * shows how much of the old volume was never seen by a human.
 *
 * Route-level: filter on `route` / `surface` and join to `paywall_viewed` for
 * paywall variant and pricing.
 */
export const contentVisible = defineEvent({
  eventName: "content_visible",
  eventVersion: 2,
  /** Route path, matching the `entrypoint_viewed` value convention. */
  valueSchema: z.string(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      route: z.string().optional(),
      surface: z.union([surfaceSchema, z.string()]).optional(),
      /** Milliseconds this checkpoint accounts for. Absent on the impression. */
      elapsed_ms: z.number().int().nonnegative().optional(),
    })
    .passthrough(),
});
